import { useCallback, useEffect, useRef } from "react";

// temp 节点 id 前缀（后端保存返回真实 id 前的本地占位）
export const isTempNodeId = (id) => typeof id === "string" && id.startsWith("node-");

/**
 * P2-3 防抖批量保存 + temp 连线队列
 *
 * - dirtyNodes: Map(id → payload|null)；payload 为 null 时 flush 从最新节点状态构建
 * - markDirty 后 delay ms 防抖 flush；拖拽结束/关页前可手动 flush
 * - 新建节点不走队列（立即 POST 拿真实 id）
 * - temp 连线：任一端还是 temp id 时先入 pendingConnections，
 *   节点真实 id 回写（flushConnectionsFor）时再补 POST
 */
export function useSaveQueue({ canvasId, onSaveNode, onSaveConnection, getNodes, stripData, delay = 800 }) {
  const dirtyRef = useRef(new Map());
  const timerRef = useRef(null);
  const pendingConnectionsRef = useRef([]);
  const flushingRef = useRef(false);

  const buildPayload = useCallback(
    (node) => ({
      id: node.id,
      canvasId,
      nodeType: node.data?.legacyType || node.type,
      positionX: node.position.x,
      positionY: node.position.y,
      data: JSON.stringify(stripData(node.data)),
    }),
    [canvasId, stripData]
  );

  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      const entries = [...dirtyRef.current.entries()];
      dirtyRef.current = new Map();
      const currentNodes = getNodes();
      await Promise.all(
        entries.map(async ([id, storedPayload]) => {
          if (isTempNodeId(id)) return; // temp 节点由创建流程自行保存
          const payload = storedPayload || (() => {
            const node = currentNodes.find((n) => n.id === id);
            return node ? buildPayload(node) : null;
          })();
          if (!payload) return; // 节点可能已被删除
          try {
            await onSaveNode(payload);
          } catch (e) {
            console.error("[Canvas] 防抖保存失败:", e);
          }
        })
      );
    } finally {
      flushingRef.current = false;
    }
  }, [getNodes, buildPayload, onSaveNode]);

  // 标脏：payload 可省（flush 时取节点最新状态）
  const markDirty = useCallback(
    (id, payload = null) => {
      if (!id || isTempNodeId(id)) return;
      dirtyRef.current.set(id, payload);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        flush();
      }, delay);
    },
    [flush, delay]
  );

  // 关页前尽力 flush（异步 fire-and-forget）
  useEffect(() => {
    const handler = () => {
      flush();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [flush]);

  // temp 连线入队：{ source, target, sourceHandle, targetHandle, connectionType }
  const queueConnection = useCallback((conn) => {
    pendingConnectionsRef.current.push(conn);
  }, []);

  // temp 节点拿到真实 id 时调用：重映射并 flush 就绪的连线
  const flushConnectionsFor = useCallback(
    async (tempId, realId) => {
      pendingConnectionsRef.current = pendingConnectionsRef.current.map((c) => ({
        ...c,
        source: c.source === tempId ? realId : c.source,
        target: c.target === tempId ? realId : c.target,
      }));
      const ready = [];
      const waiting = [];
      for (const c of pendingConnectionsRef.current) {
        if (isTempNodeId(c.source) || isTempNodeId(c.target)) waiting.push(c);
        else ready.push(c);
      }
      pendingConnectionsRef.current = waiting;
      for (const c of ready) {
        try {
          await onSaveConnection({
            canvasId,
            sourceNodeId: c.source,
            targetNodeId: c.target,
            sourcePort: c.sourceHandle || "default",
            targetPort: c.targetHandle || "default",
            connectionType: c.connectionType || "reference",
          });
        } catch (e) {
          console.error("[Canvas] 补存 temp 连线失败:", e);
        }
      }
    },
    [canvasId, onSaveConnection]
  );

  return { markDirty, flush, queueConnection, flushConnectionsFor };
}
